import { onScopeDispose } from "vue";
import { useRouter } from "vue-router";

import type { Router } from "vue-router";

import { useAzureStore } from "@/store/azure.store";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import {
  TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
  isEmbedded,
} from "@/remote/contract";
// Host -> titan only; the host owns the company (see companyScope.ts).
import {
  currentScope,
  isCompanyScope,
  isSameOrg,
  publishedScope,
  resolveTier,
  routeScope,
  type OrgRef,
} from "@/remote/companyScope";
import {
  navigateToCustomerOrganisation,
  navigateToMspOrganisation,
  navigateToSystemOrganisation,
} from "@/utils/navigation/organisationNavigation";
import type { OrgTier } from "@/utils/navigation/orgTier";

/** Never fatal: this runs before the navigation, and a stale cache beats not switching at all. */
const clearOrgScopedCaches = (): void => {
  try {
    useAzureStore().clearAzureCache();
    useLicenceStore().clearLicenceCache();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      "[titan] could not clear the org-scoped caches before switching company — switching " +
        "anyway, so some panels may show the previous company's data until they refetch.",
      error,
    );
  }
};

// Breaker on re-navigating to the same company. Rate, not count: nobody re-picks one company four
// times in three seconds, and a bounce loop does nothing else.
const LOOP_ATTEMPTS = 3;
const LOOP_WINDOW_MS = 3000;

let recentTargets: { at: number; ref: OrgRef }[] = [];

const sameRef = (left: OrgRef | null, right: OrgRef | null): boolean =>
  left !== null &&
  right !== null &&
  left.type === right.type &&
  left.uuid === right.uuid;

/** Records this attempt and reports whether the same company is being re-applied too fast. */
const isLooping = (target: OrgRef): boolean => {
  const now = Date.now();
  recentTargets = recentTargets.filter(
    (entry) => now - entry.at < LOOP_WINDOW_MS && sameRef(entry.ref, target),
  );
  recentTargets.push({ at: now, ref: target });
  return recentTargets.length > LOOP_ATTEMPTS;
};

/** DEV-only. "Titan stayed put" has four causes; each exit below says which one it took. */
const trace = (message: string, detail?: unknown): void => {
  // eslint-disable-next-line runtime-env/no-buildtime-env
  if (!import.meta.env.DEV) return;
  // eslint-disable-next-line no-console
  console.log(`[titan:scope] ${message}`, detail ?? "");
};

/** Enough to identify a scope in a console line, whatever shape the host sent. */
const describeScope = (published: unknown): string => {
  if (!isCompanyScope(published)) return "the company it published";
  return `${published.type} ${published.organisationUuid}`;
};

const NAVIGATE_TO: Record<
  OrgTier,
  (deps: { router: Router }, org: { id: string; name: string }) => Promise<void>
> = {
  msp: navigateToMspOrganisation,
  customer: navigateToCustomerOrganisation,
  system: navigateToSystemOrganisation,
};

// Long enough for any guard chain; a queue that hangs on a lost afterEach would freeze the switcher.
const NAVIGATION_SETTLE_TIMEOUT_MS = 5000;

type NavigationTracker = { pending: Promise<void> | null };

const trackers = new WeakMap<Router, NavigationTracker>();

/** Installed once per router: `pending` holds while a navigation is between its first guard and
 * its afterEach/onError. A redirect re-enters beforeEach without an afterEach in between, so it
 * stays one pending navigation. */
const trackNavigations = (router: Router): NavigationTracker => {
  const existing = trackers.get(router);
  if (existing) return existing;

  const tracker: NavigationTracker = { pending: null };
  let release: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const settle = () => {
    clearTimeout(timer);
    tracker.pending = null;
    release?.();
    release = null;
  };
  router.beforeEach(() => {
    if (tracker.pending) return;
    tracker.pending = new Promise<void>((resolve) => {
      release = resolve;
    });
    timer = setTimeout(settle, NAVIGATION_SETTLE_TIMEOUT_MS);
  });
  router.afterEach(settle);
  router.onError(settle);

  trackers.set(router, tracker);
  return tracker;
};

/**
 * Resolves once no navigation is in flight. The host publishes the scope BEFORE it dispatches the
 * popstate that starts titan's navigation, and vue-router reaches its first global guard only
 * after promise hops — so a microtask after the scope arrives, nothing is pending yet. One
 * macrotask is enough for the guard to have run.
 */
const navigationSettled = async (router: Router): Promise<void> => {
  const tracker = trackNavigations(router);
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  while (tracker.pending) await tracker.pending;
};

const applyCompanyScope = async (
  published: unknown,
  router: Router,
): Promise<void> => {
  if (!isCompanyScope(published)) {
    trace("ignored — the host published no usable scope", published);
    return;
  }

  // The check below reads the route, which is `/` until the initial navigation resolves — and
  // the previous route while a URL-driven navigation to this very company is still in flight.
  await router.isReady();
  await navigationSettled(router);

  // Both, because the store is persisted: a fresh mount reports an earlier visit's company while
  // its own boot landed elsewhere.
  if (
    isSameOrg(currentScope(), published) &&
    isSameOrg(routeScope(router), published)
  ) {
    trace(
      "already there — store and route both agree",
      published.organisationUuid,
    );
    return;
  }

  const target: OrgRef = {
    type: published.type,
    uuid: published.organisationUuid,
  };

  if (isLooping(target)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[titan] refusing to navigate to ${target.type} ${target.uuid} again — ${LOOP_ATTEMPTS}+ ` +
        `attempts in ${LOOP_WINDOW_MS}ms means something is navigating titan straight back. ` +
        "Leaving it where it is; see useCompanyScopeSync.",
    );
    return;
  }

  trace(`navigating to ${published.type} ${published.organisationUuid}`, {
    from: router.currentRoute.value.fullPath,
  });
  clearOrgScopedCaches();

  const organisation = {
    id: published.organisationUuid,
    name: published.name || "",
  };
  const tier = await resolveTier(organisation.id, published.type);
  trace(`resolved ${published.type} to titan's ${tier} tier`, organisation.id);
  await NAVIGATE_TO[tier]({ router }, organisation);
};

/** TEST ONLY — the breaker's window is module state and outlives a test case. */
export const __resetCompanyScopeSyncForTests = (): void => {
  recentTargets = [];
};

/** Follows the host's switcher while titan is on screen; the boot landing is `companyLanding.ts`.
 * No `router.go(0)` — the page is the host's, so a reload would take the shell with it. */
export function useCompanyScopeSync(): void {
  if (!isEmbedded()) return;

  const router = useRouter();
  // From here, not from the first apply: a navigation already in flight must be seen.
  trackNavigations(router);

  // Serialised: two scopes in flight would both pass the already-current check.
  let queue = Promise.resolve();
  const apply = (published: unknown) => {
    queue = queue
      .then(() => applyCompanyScope(published, router))
      // Caught so the next scope still runs, reported so a failed switch is not invisible.
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error(
          "[titan] could not follow the host to " +
            `${describeScope(published)} — staying on the current company.`,
          error,
        );
      });
  };

  // The global is current, so a company selected before this mounted is not lost.
  trace("mounted; global holds", publishedScope());
  apply(publishedScope());

  const onScopeChange = (event: Event) =>
    apply((event as CustomEvent<unknown>).detail);

  document.addEventListener(
    TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
    onScopeChange,
  );
  onScopeDispose(() =>
    document.removeEventListener(
      TITAN_REMOTE_COMPANY_SCOPE_CHANGE_EVENT,
      onScopeChange,
    ),
  );
}

export default useCompanyScopeSync;
